import React, { useCallback, useEffect, useState } from 'react'
import { getMonthlyRewardBoard, type MonthlyRewardResponse, type MonthlyRewardTabKey } from '../../services/monthlyRewardService'
import { isMonthlyRewardConfigUpdate } from '../../utils/monthlyRewardTabs'

interface RewardLeaderboardPageProps {
  tab: MonthlyRewardTabKey
}

const RewardLeaderboardPage: React.FC<RewardLeaderboardPageProps> = ({ tab }) => {
  const [data, setData] = useState<MonthlyRewardResponse['data'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await getMonthlyRewardBoard(tab)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
      }
      setData(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดข้อมูลรางวัลประจำเดือนไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [tab])

  useEffect(() => {
    void loadBoard()
  }, [loadBoard])

  useEffect(() => {
    const handleRewardRefresh = (event: Event) => {
      const detail = (event as CustomEvent)?.detail
      if (!isMonthlyRewardConfigUpdate(detail)) return
      void loadBoard()
    }

    window.addEventListener('content_update', handleRewardRefresh as EventListener)
    window.addEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)

    return () => {
      window.removeEventListener('content_update', handleRewardRefresh as EventListener)
      window.removeEventListener('monthly_reward_config_updated', handleRewardRefresh as EventListener)
    }
  }, [loadBoard])

  return (
    <div className="bdr-5 bgc-4 p-2">
      <div className="wrap justify-content-between mb-2">
        <div>
          <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-1">
            <i className="fas fa-trophy me-2" />
            {data?.title || 'รางวัลประจำเดือน'}
          </div>
          <small className="c-dark">
            {data?.monthLabel || 'เดือนปัจจุบัน'}
            {data?.subtitle ? ` • ${data.subtitle}` : ''}
          </small>
        </div>
        {data?.summary ? (
          <small className="c-dark">
            อันดับจริง {data.summary.realCount} / เติมข้อมูล {data.summary.fakeCount}
          </small>
        ) : null}
      </div>

      {loading ? (
        <div className="text-center py-4 c-dark">กำลังโหลดข้อมูล...</div>
      ) : error ? (
        <div className="text-center py-4 c-red">{error}</div>
      ) : data?.items?.length ? (
        <div className="table-responsive w-100">
          <table className="table mb-0 table-striped table-bordered text-center table-sm">
            <thead className="table-dark">
              <tr>
                <th>ลำดับ</th>
                <th>ยูสเซอร์</th>
                <th>{data.metricLabel}</th>
                <th>{data.rewardLabel}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={`${tab}-${row.rank}`}>
                  <td>{row.rank}</td>
                  <td>{row.user}</td>
                  <td>{row.metricValue}</td>
                  <td>{row.reward}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-4 c-dark">{data?.emptyText || 'ยังไม่มีข้อมูลรางวัลประจำเดือน'}</div>
      )}
    </div>
  )
}

export default RewardLeaderboardPage
